"use client";

import { ArrowRight, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/lib/trpc/react";

// Skip is only offered on the profile step; the only step reached after the
// mandatory phone verification.
type JoinSkipStep = "profile";

interface JoinSkipButtonProps {
  step: JoinSkipStep;
}

/**
 * Fixed top-right "Skip →" affordance for the /join flow. Opens a persuasive
 * modal whose hero stat makes it evident that finishing pays off, then lets the
 * user bail to Home where the missing parts resurface as important tasks.
 */
export function JoinSkipButton({ step }: JoinSkipButtonProps) {
  const tErr = useTranslations("Join.errors");
  const t = useTranslations("Join.skip");
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const exitOnboarding = api.expertProfile.exitOnboarding.useMutation({
    onSuccess: () => {
      router.push("/me/home");
    },
    onError: (err) => {
      toast.error(tErr("skip"), { description: err.message });
    },
  });

  const close = useCallback(() => {
    if (!exitOnboarding.isPending) {
      setOpen(false);
    }
  }, [exitOnboarding.isPending]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className="hover:text-foreground fixed top-10 right-5 z-20 inline-flex cursor-pointer items-center gap-1 text-[13px] font-medium text-neutral-500 transition-colors sm:right-10 sm:text-[13.5px] dark:text-neutral-400"
      >
        {t("skip")}
        <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2.2} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("dialogLabel")}
          className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center px-5 duration-150"
        >
          <button
            type="button"
            aria-label={t("close")}
            onClick={close}
            className="absolute inset-0 cursor-default bg-black/45"
          />

          <div className="animate-in zoom-in-95 border-border bg-card relative w-full max-w-[420px] rounded-[18px] border px-7 pt-7 pb-6 duration-150 sm:px-8 sm:pt-8">
            <button
              type="button"
              aria-label={t("close")}
              onClick={close}
              className="hover:bg-muted hover:text-foreground absolute top-4 right-4 inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition-colors dark:text-neutral-400"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>

            <p className="text-foreground mt-4 text-[24px] leading-[1.2] font-semibold tracking-[-0.02em] sm:text-[26px]">
              {t("statLead")}{" "}
              <span className="text-marketing-blue dark:text-marketing-blue-light">
                {t("statEmphasis")}
              </span>{" "}
              {t("statTail")}
            </p>

            <p className="mt-2.5 text-[14px] leading-[1.55] text-[#606060] dark:text-neutral-400">
              {t("reassurance")}
            </p>

            <div className="mt-6 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={close}
                disabled={exitOnboarding.isPending}
                className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[46px] w-full cursor-pointer items-center justify-center rounded-full text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("keepBuilding")}
              </button>
              <button
                type="button"
                onClick={() => {
                  exitOnboarding.mutate({ step });
                }}
                disabled={exitOnboarding.isPending}
                className="hover:text-foreground inline-flex h-[44px] w-full cursor-pointer items-center justify-center rounded-full text-[14px] font-medium text-[#606060] transition-colors disabled:cursor-not-allowed disabled:opacity-60 dark:text-neutral-400"
              >
                {exitOnboarding.isPending ? t("skipping") : t("skipAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
